import { PurchaseLabelType } from "@redotech/merchant-sdk/rpc/schema/purchase-label";
import {
  getPrettyCarrierName,
  getPrettyServiceName,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-labels";
import { Rate } from "@redotech/redo-fulfillment-carrier-common/shipment";
import { useTranslation } from "@redotech/redo-i18n";
import { MerchantAppReturn } from "@redotech/redo-return-core-common/return";
import { getIsFirstLabel } from "@redotech/redo-return-core-common/return-shipment-utils";
import { willRateHaveQrCode } from "@redotech/redo-return-core-common/shipping/qr-code";
import { Team } from "@redotech/redo-team-common/team";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext } from "react";
import { getCarrierLogo } from "../create-label-modal-utils";

export const LabelRate = memo(function LabelRate({
  labelRate,
  originalLabelCost,
  labelType,
  team,
  return_,
  isButtonDisplay = false,
}: {
  labelRate: Rate;
  originalLabelCost: number;
  labelType: PurchaseLabelType;
  team: Team;
  return_: MerchantAppReturn;
  isButtonDisplay?: boolean;
}) {
  const { t } = useTranslation();
  const { formatCurrency } = useContext(CurrencyContext);
  // Parse rate string to a number for display
  const price = parseFloat(labelRate.rate);

  let difference = price - originalLabelCost;
  if (difference < 0) {
    difference = 0;
  }
  const differenceString = formatCurrency(difference);
  const isFirstLabel = getIsFirstLabel(return_);

  const labelDeductedFromCredit = return_.labelDeductedFromCredit ?? false;

  const shouldGetDiscountedNewLabel =
    labelType === PurchaseLabelType.New &&
    isFirstLabel &&
    originalLabelCost > 0 &&
    !labelDeductedFromCredit;

  const showDifferenceView =
    labelType === PurchaseLabelType.Replace || shouldGetDiscountedNewLabel;

  return (
    <Flex dir="row" gap="md" justify="space-between" w="6xl">
      <Flex align="center" dir="row" gap="sm">
        <RedoIcon Icon={getCarrierLogo(labelRate.carrier)} size={32} />

        <Flex dir="column" gap="none">
          <Flex align="center" dir="row" gap="xxs">
            <Text fontSize="sm" fontWeight="medium">
              {getPrettyCarrierName(
                labelRate.carrier,
                labelRate.shipping_provider_type,
              )}{" "}
              {getPrettyServiceName(
                labelRate.service,
                labelRate.carrier,
                labelRate.shipping_provider_type,
              )}
            </Text>
            {!isButtonDisplay && labelRate.delivery_days && (
              <Text fontSize="sm" textColor="secondary">
                {labelRate.delivery_days === 1
                  ? t("{{count}} business day", {
                      count: labelRate.delivery_days,
                    })
                  : t("{{count}} business days", {
                      count: labelRate.delivery_days,
                    })}
              </Text>
            )}
          </Flex>
          {isButtonDisplay && labelRate.is_merchant_carrier_account && (
            <Flex dir="row" gap="xs">
              <RedoBadge color="blue" size="xs" text={t("Your rate")} />
            </Flex>
          )}
          <Flex align="center" dir="row" gap="sm">
            <Text fontSize="sm" fontWeight="medium">
              {formatCurrency(price)}
            </Text>
            {showDifferenceView && (
              <Text
                fontSize="sm"
                textColor={difference > 0 ? "error" : "success"}
              >
                {t("({{difference}} difference)", {
                  difference: differenceString,
                })}
              </Text>
            )}
            {!isButtonDisplay && labelRate.is_merchant_carrier_account && (
              <RedoBadge color="blue" size="xs" text={t("Your rate")} />
            )}
            {willRateHaveQrCode(labelRate, team.settings) && (
              <RedoBadge size="xs" text={t("QR Code")} />
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
});
